import translate from "../../../i18n/translate";
import { useEffect, useReducer } from "react";
import { sendRequest } from "../../../common/http";
import BarChart from "./BarChart";
import ParticipatingTable from "./ParticipatingTable";
import { useIntl } from "react-intl";
import ApiError from "../../common/elements/ApiError";
import {
  ORGANIZATIONS_API_ERROR,
  PROGRAMS_API_ERROR,
  STATS_API_ERROR
} from "@/features/common/constants";
export interface ParticipatingOrganizationProps {
  plan: any;
  showDownloadBtnChange: (showDownloadBtn: boolean) => void;
}

export default function ParticipatingOrganizations({
  plan,
  showDownloadBtnChange
}: ParticipatingOrganizationProps): JSX.Element {
  const initialState = {
    chartData: {
      completedCount: 0,
      completedPrgmCount: 0,
      submittedCourseCount: 0,
      inProgressCount: 0,
      inProgressPrgmCount: 0,
      inProgressCourseCount: 0,
      notStartedCount: 0,
      notStartedCourseCount: 0,
      notStartedPrgmCount: 0,
      totalParticipatingOrgs: 0,
      totalParticipatingPrgms: 0,
      totalParticipatingCourses: 0,
      totalPrograms: 0,
      totalOrganizations: 0,
      totalCourses: 0
    },
    orgHierarchy: {},
    programHierarchy: {},
    leads: [],
    statsApiError: false,
    organizationsApiError: false,
    programsApiError: false
  };

  const reducer = (state: any, action: any) => {
    switch (action.type) {
      case "setChartData":
        return { ...state, chartData: action.data };
      case "setOrgHierarchy":
        return { ...state, orgHierarchy: action.data };
      case "setProgramHierarchy":
        return { ...state, programHierarchy: action.data };
      case "setLeads":
        return { ...state, leads: action.data };
      case "setApiError":
        return setApiError(state, action.errorType);
      default:
        return state;
    }
  };

  const setApiError = (state: any, errorType: string) => {
    switch (errorType) {
      case ORGANIZATIONS_API_ERROR: {
        return { ...state, organizationsApiError: true };
      }
      case PROGRAMS_API_ERROR: {
        return { ...state, programsApiError: true };
      }
      case STATS_API_ERROR: {
        return { ...state, statsApiError: true };
      }
      default: {
        return { ...state };
      }
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const applyResult = (result: any) => {
      dispatch({ type: "setChartData", data: result });
    };

    const onError = () => {
      dispatch({
        ...state,
        type: "setApiError",
        errorType: STATS_API_ERROR
      });
      showDownloadBtnChange(false);
    };
    if (plan.uuid) {
      sendRequest(
        { uri: `/plan/${plan.uuid}/stats`, camelize: true },
        applyResult,
        onError
      );
    }
  }, [plan]);

  useEffect(() => {
    const applyOrgHierarchyResult = (result: any) => {
      dispatch({ type: "setOrgHierarchy", data: result });
    };
    const onError = () => {
      dispatch({
        ...state,
        type: "setApiError",
        errorType: ORGANIZATIONS_API_ERROR
      });
    };
    if (plan.uuid && state.chartData.totalParticipatingOrgs > 0) {
      sendRequest(
        {
          uri: `/hierarchy/participating_organizations/${plan.uuid}`,
          camelize: true
        },
        applyOrgHierarchyResult,
        onError
      );
    }
  }, [plan, state.chartData.totalParticipatingOrgs]);

  useEffect(() => {
    const applyProgramHierarchyResult = (result: any) => {
      dispatch({ type: "setProgramHierarchy", data: result });
    };

    const onError = () => {
      dispatch({
        ...state,
        type: "setApiError",
        errorType: PROGRAMS_API_ERROR
      });
    };
    if (plan.uuid && state.chartData.totalParticipatingPrgms > 0) {
      sendRequest(
        {
          uri: `/hierarchy/participating_programs/${plan.uuid}`,
          camelize: true
        },
        applyProgramHierarchyResult,
        onError
      );
    }
  }, [plan, state.chartData.totalParticipatingPrgms]);

  useEffect(() => {
    const applyLeadsResult = (result: any) => {
      dispatch({ type: "setLeads", data: result });
    };

    sendRequest({ uri: "/leads", camelize: true }, applyLeadsResult);
  }, []);

  const participatingProgramsNotStarted = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsNotStarted"
  });
  const participatingProgramsInProgress = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsInProgress"
  });

  const participatingProgramsInComplete = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsInComplete"
  });

  const participatingProgramsCompleted = useIntl().formatMessage({
    id: "app.planParticipation.participatingProgramsSubmitted"
  });
  const participatingOrgsNotStarted = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgsNotStarted"
  });
  const participatingOrgsInProgres = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgsInProgres"
  });
  const participatingOrgsInComplete = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgsInComplete"
  });

  const participatingOrgsCompleted = useIntl().formatMessage({
    id: "app.planParticipation.participatingOrgsSubmitted"
  });

  const participatingCoursesNotStarted = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesNotStarted"
  });
  const participatingCoursesInProgress = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesInProgress"
  });

  const participatingCoursesInComplete = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesInComplete"
  });

  const participatingCoursesCompleted = useIntl().formatMessage({
    id: "app.planParticipation.participatingCoursesSubmitted"
  });

  const inProgress = useIntl().formatMessage({
    id: "app.drilldown.inProgress"
  });

  const inComplete = useIntl().formatMessage({
    id: "app.drilldown.inComplete"
  });

  const planCourseBar = (status: number) => {
    let [popupText, legend] =
      status == 1
        ? [participatingCoursesInComplete, inComplete]
        : [participatingCoursesInProgress, inProgress];

    if (
      state.chartData.totalParticipatingCourses > 0 &&
      plan.assessmentTypeId === 1
    ) {
      return (
        <>
          <div className="chart-heading">
            {translate("app.planParticipation.participatingCoursesByStatus")}
          </div>
          <div className="flex-row bar-content">
            <div className="counts dropnumber">
              <p className="count">
                {state.chartData.totalParticipatingCourses.toString() +
                  "/" +
                  state.chartData.totalCourses.toString()}
              </p>

              <p className="details">
                {translate("app.planParticipation.courseParticipating")}
              </p>
            </div>
            <BarChart
              notStartedCount={state.chartData.notStartedCourseCount.toString()}
              inProgressCount={state.chartData.inProgressCourseCount.toString()}
              submittedCount={state.chartData.submittedCourseCount.toString()}
              totalCount={state.chartData.totalParticipatingCourses.toString()}
              notStartedText={participatingCoursesNotStarted}
              inProgressText={popupText}
              inProgressLegend={legend}
              completedText={participatingCoursesCompleted}
            />
          </div>
        </>
      );
    }
  };

  const planOrgBar = (status: number) => {
    let [popupText, legend] =
      status == 1
        ? [participatingOrgsInComplete, inComplete]
        : [participatingOrgsInProgres, inProgress];

    if (
      state.chartData.totalParticipatingOrgs > 0 &&
      plan.assessmentTypeId === 0
    ) {
      return (
        <>
          <div className="chart-heading">
            {translate("app.planParticipation.participatingOrgs")}
          </div>
          <div className="flex-row">
            <div className="counts dropnumber">
              <p className="count">
                {state.chartData.totalParticipatingOrgs.toString() +
                  "/" +
                  state.chartData.totalOrganizations.toString()}
              </p>
              <p className="details">
                {translate("app.planParticipation.orgParticipating")}
              </p>
            </div>
            <BarChart
              notStartedCount={state.chartData.notStartedCount.toString()}
              inProgressCount={state.chartData.inProgressCount.toString()}
              submittedCount={state.chartData.completedCount.toString()}
              totalCount={state.chartData.totalParticipatingOrgs.toString()}
              notStartedText={participatingOrgsNotStarted}
              inProgressText={popupText}
              inProgressLegend={legend}
              completedText={participatingOrgsCompleted}
            />
          </div>
        </>
      );
    }
  };
  const planPrgmBar = (status: number) => {
    let [popupText, legend] =
      status == 1
        ? [participatingProgramsInComplete, inComplete]
        : [participatingProgramsInProgress, inProgress];
    if (
      state.chartData.totalParticipatingPrgms > 0 &&
      plan.assessmentTypeId === 0
    ) {
      return (
        <>
          <div className="chart-heading">
            {translate("app.planParticipation.participatingProgramsByStatus")}
          </div>
          <div className="flex-row">
            <div className="counts dropnumber">
              <p className="count">
                {state.chartData.totalParticipatingPrgms.toString() +
                  "/" +
                  state.chartData.totalPrograms.toString()}
              </p>
              <p className="details">
                {translate("app.planParticipation.programsParticipating")}
              </p>
            </div>
            <BarChart
              notStartedCount={state.chartData.notStartedPrgmCount.toString()}
              inProgressCount={state.chartData.inProgressPrgmCount.toString()}
              submittedCount={state.chartData.completedPrgmCount.toString()}
              totalCount={state.chartData.totalParticipatingPrgms.toString()}
              notStartedText={participatingProgramsNotStarted}
              inProgressText={popupText}
              inProgressLegend={legend}
              completedText={participatingProgramsCompleted}
            />
          </div>
        </>
      );
    }
  };

  return (
    <section
      className="-mainheader -withbreadcrumbs only-breadcrumbs body-padding"
      id="page-header"
    >
      {state.statsApiError ? (
        <ApiError containerClasses="api-error" />
      ) : (
        <div className="body">
          <div
            className={
              state.chartData.totalParticipatingOrgs &&
              state.chartData.totalParticipatingPrgms
                ? "flex-row insightsgrid border-bottom"
                : "flex-row insightsgrid"
            }
          >
            {planCourseBar(plan.status)}
            {planOrgBar(plan.status)}
            {state.chartData.totalParticipatingOrgs ? (
              <ParticipatingTable
                orgHierarchy={state.orgHierarchy}
                leads={state.leads}
                heading={translate("app.planParticipation.progressOrgs")}
                planStatus={plan.status}
                apiError={state.organizationsApiError}
              />
            ) : (
              <></>
            )}
          </div>
          <div className="flex-row insightsgrid">
            {planPrgmBar(plan.status)}
            {state.chartData.totalParticipatingPrgms &&
            plan.assessmentTypeId === 0 ? (
              <ParticipatingTable
                orgHierarchy={state.programHierarchy}
                leads={state.leads}
                heading={translate("app.planParticipation.progressPrgm")}
                planStatus={plan.status}
                apiError={state.programsApiError}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
